import type { HTMLAttributes, ReactNode } from "react";

export function SettingsHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-5">
      <h2 className="text-[20px] font-semibold tracking-tight text-foreground">
        {title}
      </h2>
      {description ? (
        <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>
      ) : null}
    </div>
  );
}

export function FormCard({
  children,
  ...rest
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card"
      {...rest}
    >
      {children}
    </div>
  );
}

export function FormRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,320px)] items-center gap-6 px-4 py-4">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-foreground">{label}</div>
        {hint ? (
          <div className="mt-0.5 text-[12px] text-muted-foreground">{hint}</div>
        ) : null}
      </div>
      <div className="flex items-center justify-end">{children}</div>
    </div>
  );
}

export function FormStackedRow({
  label,
  hint,
  children,
}: {
  label?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2 px-4 py-4">
      {label ? (
        <div>
          <div className="text-[13px] font-medium text-foreground">{label}</div>
          {hint ? (
            <div className="mt-0.5 text-[12px] text-muted-foreground">
              {hint}
            </div>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

export function InlineMessage({
  variant,
  children,
}: {
  variant: "error" | "success" | "info";
  children: ReactNode;
}) {
  const palette =
    variant === "error"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : variant === "success"
        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
        : "border-border bg-muted/40 text-foreground";
  return (
    <div
      className={`rounded-xl border px-4 py-3 text-[13px] leading-relaxed ${palette}`}
    >
      {children}
    </div>
  );
}
